import {AgGridReact} from "ag-grid-react";
import React, {useCallback, useEffect, useRef, useState} from "react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import './DiagnosticStyles.css';
import {calculateRoundPercent, cellStyleGrowthPercentage, cellStylePercentage, PercentageCell} from './Utils.js';
import {
    ChartLineColor,
    COL_DEFS,
    defaultPDFParams,
    GRID_ENUMS
} from "./Styles";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {printDoc} from "./pdfExport/printDoc";
import Tooltip from "../common/Tooltip";
import IconButton from "../common/IconButton";
import LineChartLegend, {LEGEND_SHAPES} from "./LineChartLegend";
import ScatterChartComponent from "./ScatterChartComponent";
import {GROWTH_FILTER, LOCATION_AGGREGATE, TEST_GROUP_TEST_TYPES} from "../tests/teacher/Diagnostics/Reports/TestGroupReports";

const GrowthVsAttainment = props => {
    const {isLoading, setLoading, testAssignmentsData, students, studentIds, selectedTestIds,
            selectedDifficultyIds,
            showGrowthLine, showAverageScoreLine,
            openDialogCallback, triggerGenerate,
            studentGrowthFilter,
            averageScoreSelect,
            growthSelect,
            scoreAverageAggregate,
            growthAggregate,
            selectedAverageGrowth,
            selectedAverageScore
        } = props,
        preTestPercentFieldName = "preTestScorePercent",
        postTestPercentFieldName = "postTestScorePercent",
        growthPercentageFieldName = "growthPercentage",
        postTest = 'Post Test',
        rowHeight = 30,
        headerHeight = 32,
        REPORT_NAME = 'GrowthVsAttainmentReport';

    const labelDisplayMap = {
        value:'% correct',
        difference:'% growth'
    }

    const StudentCustomCell = props => {
        const {data, value} = props;
        const onClick = () => {
            if (typeof data.callback === 'function') {
                data.callback(data.studentId);
            }
        }

        return (
            <div className={'custom-cell-cont'}>
                <Tooltip title={value} placement="left" enterNextDelay={100}>
                    <div className={'label'}>{value}</div>
                </Tooltip>
                <IconButton onClick={onClick} tooltip={'Show Student Test/s'} placement={'left'}>
                    <span className="material-icons">open_in_new</span>
                </IconButton>
            </div>
        )
    }

    const columnDefs = [
        {
            flex:1,
            headerName:'Student',
            headerClass: 'text-center-align',
            cellStyle:{
                textAlign:'left',
                borderRight:'1px solid lightgrey'
            },
            field:'name',
            sortable:true,
            unSortIcon: true,
            wrapText:true,
            cellRenderer: StudentCustomCell
        },
        {
            flex:1,
            sortable: true,
            unSortIcon: true,
            headerName:'Pre/mid-test(s) score',
            field: preTestPercentFieldName,
            headerClass: 'text-center-align',
            cellStyle: cellStylePercentage,
            cellRenderer: PercentageCell
        },
        {
            flex:1,
            sortable: true,
            unSortIcon: true,
            headerName: 'Post-test score',
            field: postTestPercentFieldName,
            headerClass: 'text-center-align',
            cellStyle: cellStylePercentage,
            cellRenderer: PercentageCell
        },
        {
            flex:1,
            sortable: true,
            unSortIcon: true,
            headerName: 'Growth percentage',
            field: growthPercentageFieldName,
            headerClass: 'text-center-align',
            cellStyle: cellStyleGrowthPercentage,
            cellRenderer: PercentageCell
        }
    ];

    const growthLegendData = [
        {
            text:'Post test', colors:{
                background: ChartLineColor.POST_TEST,
                border:ChartLineColor.POST_TEST
            }, shape: LEGEND_SHAPES.CIRCLE,
        },
        {
            text:'Average growth', colors:{
                    background: ChartLineColor.AVERAGE_GROWTH_LINE,
                    border:ChartLineColor.AVERAGE_GROWTH_LINE
                },
            shape: LEGEND_SHAPES.DASH
            },
        {
            text:'Average score', colors:{
                background: ChartLineColor.AVERAGE_SCORE_LINE,
                border:ChartLineColor.AVERAGE_SCORE_LINE
            }, shape: LEGEND_SHAPES.DASH
        }
        // ,{
        //     text:'Diamond test', colors:{
        //         background: ChartLineColor.MEAN,
        //         border:ChartLineColor.MEAN
        //     }, shape: LEGEND_SHAPES.DIAMOND
        // }
    ]

    const gridRef = useRef(),
        [graphData,setGraphData] = useState(null),
        [chartData,setChartData] = useState(null),
        referenceLineXColor = ChartLineColor.AVERAGE_GROWTH_LINE,
        referenceLineYColor = ChartLineColor.AVERAGE_SCORE_LINE,
        [referenceLineXValue, setReferenceLineXValue] = useState(0),
        [referenceLineYValue, setReferenceLineYValue] = useState(0),
        [studentSeriesMap,setStudentSeriesMap] = useState({}),
        [unfilteredData, setUnfilteredData] = useState([]),
        [unfilteredChartData, setUnfilteredChartData] = useState([]),
        [questionResults, setQuestionResults] = useState([]),
        [testResultsMap, setTestResultsMap] = useState({}),
        [adaptive, setAdaptive] = useState(false);

    const onGridReady = ()=>{
        // console.log('onGridReady');
        showHideUserRows();
        setLoading(false);
        // gridRef.current.columnApi.autoSizeAllColumns();
    }

    useEffect(()=>{
        if(growthAggregate !== null){
            setReferenceLineYValue(growthAggregate);
        }else if(selectedAverageGrowth){
            setReferenceLineYValue(selectedAverageGrowth);
        }
    },[growthAggregate]);

    useEffect(()=>{
        if(scoreAverageAggregate !== null){
            setReferenceLineXValue(scoreAverageAggregate);
        }else if(selectedAverageScore !== null){
            setReferenceLineXValue(selectedAverageScore);
        }
    },[scoreAverageAggregate]);


    const onChartReady = ()=>{
        showHideUserRows();
        setLoading(false);
        // console.log('onChartReady')
        // gridRef.current.columnApi.autoSizeAllColumns();
    }

    const showHideUserRows = ()=>{
        let selectedData = [],
            selectedChartData = [];
            selectedData = unfilteredData.filter(row => studentIds.includes(row.studentId));
            selectedChartData = unfilteredChartData.filter(row => studentIds.includes(row.userId));

        const {averageGrowth, averageScore} = calculateReferenceLineMeans(selectedChartData);

        const studentGrowthFilteredData = [];

        if(averageGrowth && averageScore){
            selectedChartData.forEach((row)=>{
                let addToChart;
                if(GROWTH_FILTER.ABOVE_AVERAGE_GROWTH.val === studentGrowthFilter){
                    addToChart = row.difference > averageGrowth;
                }else if(GROWTH_FILTER.BELOW_AVERAGE_GROWTH.val === studentGrowthFilter){
                    addToChart = row.difference < averageGrowth;
                }else if(GROWTH_FILTER.ABOVE_AVERAGE_SCORE.val === studentGrowthFilter){
                    addToChart = row.value > averageScore;
                }else if(GROWTH_FILTER.BELOW_AVERAGE_SCORE.val === studentGrowthFilter){
                    addToChart = row.value < averageScore;
                }else if(GROWTH_FILTER.WITH_POSITIVE_GROWTH.val === studentGrowthFilter){
                    addToChart = row.difference > 0;
                }else if(GROWTH_FILTER.WITH_NEGATIVE_GROWTH.val === studentGrowthFilter){
                    addToChart = row.difference < 0;
                }else{ // for All
                    addToChart = true;
                }
                // console.log('addToChart: ',addToChart);
                if(addToChart){
                    studentGrowthFilteredData.push(row);
                }
            });
        }

        setGraphData(selectedData);
        if(averageGrowth && averageScore){
            setChartData(studentGrowthFilteredData);
        }else{
            setChartData(selectedChartData);
        }
    }

    // const hideAverageLine = (averageScoreSelect === LOCATION_AGGREGATE.CLASS.val && scoreAverageAggregate === null),
    //     hideGrowthLine = (growthSelect === LOCATION_AGGREGATE.CLASS.val && growthAggregate === null);
    //

    const calculateReferenceLineMeans = dataArr =>{
        if (students.length === 0 || dataArr.length === 0 || Object.keys(studentSeriesMap).length === 0) {
            return {};
        }

        let growthPercentTotal = 0,
            preTestTotal = 0,
            postTestTotal = 0,
            preTestCount = 0,
            postTestCount = 0,
            bestScore = 0;

        dataArr.forEach(row =>{
            // if(row.test === preTest && studentSeriesMap[row.series].preTestAnswered){
            //     preTestTotal += row.value;
            //     preTestCount++;
            // }else
            if(row.test === postTest && studentSeriesMap[row.series].postTestAnswered){
                growthPercentTotal += row.difference;
                postTestTotal += row.value;
                postTestCount++;

            }
            // console.log('row: ',row);
            bestScore = Math.max(bestScore,Math.round(row.value));
        });

        const updateLineXValue = Math.round(postTestTotal / postTestCount),
            updateLineYValue = Math.round(growthPercentTotal / postTestCount);

        setReferenceLineXValue(updateLineXValue);
        setReferenceLineYValue(updateLineYValue);

        return {averageGrowth: updateLineYValue, averageScore: updateLineXValue}

    }

    useEffect(()=>{
        showHideUserRows();
    },[studentIds,studentGrowthFilter]);

    useEffect(()=>{
        // console.log('before return');
        if(selectedTestIds === undefined || selectedTestIds.length === 0 || testAssignmentsData.length === 0){
            return;
        }
        // console.log('after return');
        setGraphData(null);
        setChartData(null);
        setLoading(true);

        if(testAssignmentsData.length > 0){
            let tempQuestionResults = [],
                tempTestResultsMap = {},
                isAdaptive = false;

            testAssignmentsData.forEach((testAssignmentData) => {
                let testType = testAssignmentData.testPurpose === 'DIAGNOSTIC_POST_TEST' ? TEST_GROUP_TEST_TYPES.POST_TEST : TEST_GROUP_TEST_TYPES.PRE_TEST;
                if(testAssignmentData.questionResultBeans != undefined) {
                    tempQuestionResults = [...tempQuestionResults, ...testAssignmentData.questionResultBeans];
                }

                testAssignmentData.testResultBeans.forEach((testResult) => {
                    if (!isAdaptive && testResult.adaptive) setAdaptive(true);
                    if (!tempTestResultsMap.hasOwnProperty(testResult.userId)) {
                        tempTestResultsMap[testResult.userId] = {preTest: null, postTest: null};
                    }

                    tempTestResultsMap[testResult.userId][testType] = testResult;
                });
            });

            setTestResultsMap(tempTestResultsMap);
            setQuestionResults(tempQuestionResults);
            setLoading(false);
        }else{
            setLoading(false);
        }
    },[triggerGenerate]);

    useEffect(()=>{
        // console.log('[questionResults, selectedDifficultyIds, textValues, curriculumValues]');
        if(questionResults.length > 0){
            setGraphData(null);
            setChartData(null);
            // setLoading(true);
            mapQuestionResults();
        }
    },[questionResults, selectedDifficultyIds]);



    const mapQuestionResults = ()=>{
        // console.log('mapQuestionResults');
        let studentResults = {};
        let textIncludedMap = {syllabusIds:[], topicIds:[], conceptIds:[]};
        let curriculumIncludedMap = {syllabusIds:[], topicIds:[], conceptIds:[]};
        students.forEach(student=>{
            let preTestEntry = testResultsMap[student.userId][TEST_GROUP_TEST_TYPES.PRE_TEST],
                postTestEntry = testResultsMap[student.userId][TEST_GROUP_TEST_TYPES.POST_TEST],
                preTestObj = {
                    testMark: preTestEntry !== null ? preTestEntry.mark : 0,
                    testMaxMark: preTestEntry !== null ? preTestEntry.maxMark : 0,
                    testPercentVal: preTestEntry !== null ? preTestEntry.percentCorrect : 0,
                    timeTaken: preTestEntry !== null ? preTestEntry.timeTaken : 0,
                    answered: preTestEntry !== null && preTestEntry.completeDate !== undefined ? true: false
                },
                postTestObj = {
                    testMark: postTestEntry !== null ? postTestEntry.mark : 0,
                    testMaxMark: postTestEntry !== null ? postTestEntry.maxMark : 0,
                    testPercentVal: postTestEntry !== null ? postTestEntry.percentCorrect : 0,
                    timeTaken: postTestEntry !== null ? postTestEntry.timeTaken : 0,
                    answered: postTestEntry !== null && postTestEntry.completeDate !== undefined ? true: false
                }

            studentResults[student.userId] = {
                name:student.lastName+', '+student.firstName,
                initials:(student.lastName.substr(0,1)+student.firstName.substr(0,1)).toUpperCase(),
                preTest:preTestObj,
                postTest:postTestObj
            };
        });

        if (adaptive) {
            questionResults.forEach((questionResult,idx) =>{
                if(questionResult.testPurpose === 'DIAGNOSTIC_POST_TEST'){
                    addAdaptiveDataToStudent(studentResults, questionResult,TEST_GROUP_TEST_TYPES.POST_TEST);
                }else if(questionResult.testPurpose === 'DIAGNOSTIC_PRE_TEST'){
                    addAdaptiveDataToStudent(studentResults, questionResult,TEST_GROUP_TEST_TYPES.PRE_TEST);
                }
            });
        }

        let graphArr = [],
            chartArr = [],
            studentDifferenceMap = {};
        for (const [key, value] of Object.entries(studentResults)) {
            let rowData = {},
                addToData = true;
            rowData.studentId = parseInt(key);
            rowData.name = value.name;
            rowData.initials = value.initials;
            rowData.testIds = selectedTestIds;
            rowData.callback = openDialogCallback;

            let tablePrePercentVal,
                tablePostPercentVal,
                chartPrePercentVal = 0,
                chartPostPercentVal = 0,
                chartPreHoverDisplay = GRID_ENUMS.NOT_ATTEMPTED,
                chartPostHoverDisplay = GRID_ENUMS.NOT_ATTEMPTED;

            if (value.preTest.answered) {
                chartPrePercentVal = tablePrePercentVal = value.preTest.testPercentVal;
                chartPreHoverDisplay = value.preTest.testPercentVal + '%';
            }
            if (value.postTest.answered) {
                chartPostPercentVal = tablePostPercentVal = value.postTest.testPercentVal;
                chartPostHoverDisplay = value.postTest.testPercentVal + '%';
            }

            rowData[preTestPercentFieldName] = tablePrePercentVal;
            rowData[postTestPercentFieldName] = tablePostPercentVal;
            let differenceVal = 0;
            studentDifferenceMap[GRID_ENUMS.USER + rowData.studentId] = {
                differenceVal: null,
                preTestAnswered: value.preTest.answered,
                postTestAnswered: value.postTest.answered
            }
            if (!isNaN(rowData[postTestPercentFieldName]) && !isNaN(rowData[preTestPercentFieldName])) {
                differenceVal = Math.round(value.postTest.testPercentVal - value.preTest.testPercentVal);
                studentDifferenceMap[GRID_ENUMS.USER + rowData.studentId].differenceValue = differenceVal;
                rowData[growthPercentageFieldName] = differenceVal;
            }
            rowData.differenceVal = differenceVal;
            rowData.preTestAnswered = value.preTest.answered;
            rowData.posTestAnswered = value.postTest.answered;

            // chartArr.push({
            //     userId: rowData.studentId,
            //     series: GRID_ENUMS.USER + rowData.studentId,
            //     name: rowData.name,
            //     test: preTest,
            //     value: chartPrePercentVal,
            //     difference: differenceVal,
            //     testOrder: 0,
            //     hoverDisplay: chartPreHoverDisplay,
            //     answered: value.preTest.answered
            // });
            // console.log('rowData: ',rowData);
            // console.log('studentGrowthFilter: ',studentGrowthFilter);

            if(addToData){
                chartArr.push({
                    userId: rowData.studentId,
                    series: GRID_ENUMS.USER + rowData.studentId,
                    name: rowData.name,
                    test: postTest,
                    value: chartPostPercentVal,
                    difference: differenceVal,
                    testOrder: 1,
                    hoverDisplay: chartPostHoverDisplay,
                    answered: value.postTest.answered,
                    x: chartPostPercentVal,
                    y: differenceVal,
                    label:rowData.initials
                });
                graphArr.push(rowData);
            }
        }
        //sort in order so pre Test shows before post test on the graph even if post test result comes first
        // chartArr.sort((a, b) =>{
        //     return a.testOrder - b.testOrder;
        // });

        calculateReferenceLineMeans(chartArr);

        setStudentSeriesMap(studentDifferenceMap);
        setChartData(chartArr);
        setUnfilteredChartData(chartArr);
        setGraphData(graphArr);
        setUnfilteredData(graphArr);
    }

    // useEffect(()=>{
    //     if(graphData !== null && chartData !== null){
    //         setLoading(false);
    //     }
    // },[graphData,chartData]);

    const addAdaptiveDataToStudent = (map,result,testType)=>{
        if(map.hasOwnProperty(result.userId) && map[result.userId].hasOwnProperty(testType)){
            map[result.userId][testType].testMaxMark += result.adaptiveMaxMark !== 0 ? result.adaptiveMaxMark : result.maxMark;
            map[result.userId][testType].testPercentVal = calculateRoundPercent(map[result.userId][testType].testMark / map[result.userId][testType].testMaxMark);
        }
    }

    const onExportCSV = useCallback(() => {
        gridRef.current.api.exportDataAsCsv({fileName:REPORT_NAME});
    }, []);

    const onExportPDF = useCallback(() => {
        printDoc(defaultPDFParams,gridRef.current.api,REPORT_NAME);
    }, []);

    const getToolBar = () => (
        <div className="export-toolbar genBtn-blk">
            <button type="button" className="btn btn-default excel genBtn smallBtn"
                    onClick={onExportCSV}
            >Export CSV <FileDownloadIcon/>
            </button>
            <button type="button" className="btn btn-default reset genBtn smallBtn"
                    onClick={onExportPDF}
            >Export PDF <FileDownloadIcon/>
            </button>
        </div>
    );

    return(
        <div className="growth-vs-attainment-view flexColumn">
            {!isLoading &&
                <>
                    {chartData != null &&
                        <div>
                            <LineChartLegend data={growthLegendData} showAsCard={true} cardColor={'#FAFAFB'} direction={'horizontal'} style={{width: '20%', justifyContent: 'center'}}/>
                            <div style={{marginTop: '30px', height: '400px', display: 'flex', gap: '10px'}}>
                                <ScatterChartComponent
                                    data={chartData}
                                    yTickGap={5}
                                    yAxisShowFirstVal={true}
                                    xAxisMiddle={true}
                                    referenceLineXColor={referenceLineXColor}
                                    referenceLineYColor={referenceLineYColor}
                                    referenceLineXValue={referenceLineXValue}
                                    referenceLineYValue={referenceLineYValue}
                                    // lineXVisible={(showGrowthLine && !hideGrowthLine)}
                            // lineYVisible={(showAverageScoreLine && !hideAverageLine)}
                            lineXVisible={showGrowthLine}
                            lineYVisible={showAverageScoreLine}
                            xLabel={'Percent correct on post-test (%)'} yLabel={'Growth percent (%)'}
                            labelDisplayMap={labelDisplayMap}
                        />
                    </div>
                </div>
            }
            {graphData != null &&
                <div className={'graph'}>
                            <div className={'data-set-header'}>
                                <h5>Data-set</h5> {getToolBar()}
                            </div>
                            <div className="ag-theme-balham ch-general-table-ag-grid"
                                 style={{width:'100%', height: (rowHeight * graphData.length + headerHeight + 10) + 'px'}}>
                                <AgGridReact
                                    style={{width:'100%'}}
                                    ref={gridRef}
                                    suppressPropertyNamesCheck={true}
                                    suppressHorizontalScroll={true}
                                    suppressRowTransform={true}
                                    rowData={graphData}
                                    columnDefs={columnDefs}
                                    defaultColDef={COL_DEFS.defaultColDef}
                                    onGridReady={onGridReady}
                                    // onRowClicked={handleRowClick}
                                    rowHeight={rowHeight}
                                />
                            </div>
                        </div>
                    }
                </>
            }
        </div>
    )
}
export default GrowthVsAttainment;
